import {
  Button,
  MenuItem,
  Select,
  TextField,
  FormHelperText,
  Checkbox,
} from "@mui/material";
import EditText from "components/editText";
import { Loader, showError, showToast } from "helpers";
import MainContainer from "layout/MainContainer";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLazyGetAllCategoryQuery } from "service/category";
import {
  useAddSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useLazySubscriptionDetailsQuery,
} from "service/subscription";
import { useFormik } from "formik";
import * as Yup from "yup";
import { generateEncryptedKeyBody } from "utils/crypto";
import { CommonBody } from "types/General";
import { PAYMENT_METHOD, PLAN_TYPE } from "utils/enums";
import {
  useLazyGetAllCitsQuery,
  useLazyGetAllVehiclesQuery,
} from "service/vehicles";
import { isNumber } from "utils/validations";

interface FormValues {
  planName: string;
  planAmount: string;
  planDuration: string;
  mileageLimit: string;
  allowedLocations: string;
  vehicleCategory: string;
  additionalBenefits: string;
  vehicle: string[];
  discountAmount: string;
}

const AddSubscription = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  type Category = { _id: string; name: { en: string } };
  const [categories, setCategories] = useState<Category[]>([]);
  const [getCategories] = useLazyGetAllCategoryQuery();
  const [addPlan] = useAddSubscriptionMutation();
  const [updatePlan] = useUpdateSubscriptionMutation();
  const [planDetails] = useLazySubscriptionDetailsQuery();
  const [getCities] = useLazyGetAllCitsQuery();
  const [cities, setCities] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const { id } = useParams();
  const [getVehicles] = useLazyGetAllVehiclesQuery();
  const [locations] = useState([
    { _id: "64b2f52ddcbad24f12345678", name: "Location 1" },
    { _id: "64b2f52ddcbad24f12345679", name: "Location 2" },
  ]);

  const getCitiesApi = async () => {
    setLoading(true);
    try {
      const response = await getCities({}).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setCities(response?.data?.result);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getCategoriesApi = async () => {
    setLoading(true);
    try {
      const response = await getCategories({
        page: 1,
        search: "",
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setCategories(response?.data?.list);
      }
    } catch (error) {
      setLoading(false);
      showError((error as any)?.data?.message || "Failed to process request");
    }
  };

  const getVehiclesApi = async () => {
    setLoading(true);
    try {
      const response = await getVehicles({
        page: 1,
        search: "",
        isSubscription: true,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setVehicles(response?.data?.vehicles);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const response = await planDetails({ id }).unwrap();
      if (response?.statusCode == 200) {
        formik.setValues({
          planName: response?.data?.planName || "",
          planAmount: response?.data?.planAmount || "",
          mileageLimit: response?.data?.mileageLimit || "",
          allowedLocations: response?.data?.allowedLocations[0] || "",
          vehicleCategory: response?.data?.vehicleCategory || "",
          paymentMethod: response?.data?.paymentMethod || "",
          additionalBenefits: response?.data?.additionalBenefits[0] || "",
          vehicle: response?.data?.carIds || false,
          planDuration: response?.data?.planType || "",
          discountAmount: response?.data?.discountAmount || "",
        });
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
    getCitiesApi();
    getCategoriesApi();
    getVehiclesApi();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      planName: "",
      planAmount: "",
      planDuration: "",
      mileageLimit: "",
      allowedLocations: "",
      vehicleCategory: "",
      additionalBenefits: "",
      vehicle: [],
      discountAmount: "",
    } as FormValues,
    validationSchema: Yup.object({
      planName: Yup.string().required("Plan name is required"),
      planAmount: Yup.number()
        .typeError("Amount must be a number")
        .required("Amount is required"),
      planDuration: Yup.number()
        .typeError("Duration must be a number")
        .required("Duration is required"),
      mileageLimit: Yup.number()
        .typeError("Mileage limit must be a number")
        .required("Mileage limit is required"),
      allowedLocations: Yup.string().required("Allowed location is required"),
      vehicleCategory: Yup.string().required("Vehicle category is required"),
      additionalBenefits: Yup.string().required("Additional benefits required"),
    }),
    onSubmit: async (values: FormValues) => {
      setLoading(true);
      try {
        const body = {
          planName: values.planName,
          planAmount: Number(values.planAmount),
          planType: Number(values.planDuration),
          mileageLimit: Number(values.mileageLimit),
          allowedLocations: values.allowedLocations,
          vehicleCategory: values.vehicleCategory,
          additionalBenefits: values.additionalBenefits,
          carIds: values.vehicle,
          discountAmount: Number(values.discountAmount),
        };
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updatePlan({ body: encryptedBody, id }).unwrap();
        } else {
          response = await addPlan({ body: encryptedBody }).unwrap();
        }
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast(
            id ? "Plan updated successfully" : "Plan added successfully"
          );
          navigate("/subscriptions/manage-subscriptions");
        }
      } catch (error) {
        setLoading(false);
        showError((error as any)?.data?.message || "Failed to process request");
      }
    },
  });

  console.log(formik.values.vehicle);

  const vehicleValue = Array.isArray(formik.values.vehicle)
    ? formik.values.vehicle
    : typeof formik.values.vehicle === "string" && formik.values.vehicle !== ""
      ? formik.values.vehicle.split(",")
      : [];

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit Subscription" : "Add Subscription"}</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
            Categories - {id ? "Edit Subscription" : "Add Subscription"}
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Plan Name</label>
              <TextField
                variant="outlined"
                placeholder="Name"
                className="text_field"
                fullWidth
                hiddenLabel
                name="planName"
                id="planName"
                inputProps={{ maxLength: 80 }}
                value={formik.values.planName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.planName && formik.errors.planName}
                error={
                  formik.touched.planName && Boolean(formik.errors.planName)
                }
              />
            </div>

            <div className="control_group w_50">
              <label>Duration</label>

              <Select
                fullWidth
                labelId="planDuration"
                name="planDuration"
                value={formik.values.planDuration}
                displayEmpty
                onChange={(e) =>
                  formik.setFieldValue("planDuration", e.target.value)
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.planDuration &&
                  Boolean(formik.errors.planDuration)
                }
              >
                <MenuItem disabled value="">
                  Select Duration
                </MenuItem>
                <MenuItem value={PLAN_TYPE.MONTHLY}>Monthly</MenuItem>
                <MenuItem value={PLAN_TYPE.QUARTERLY}>Quarterly</MenuItem>
                <MenuItem value={PLAN_TYPE.HALF_YEARLY}>Half Yearly</MenuItem>
                <MenuItem value={PLAN_TYPE.YEARLY}>Yearly</MenuItem>
              </Select>
              {formik.touched.planDuration && formik.errors.planDuration && (
                <FormHelperText error>
                  {formik.errors.planDuration}
                </FormHelperText>
              )}
            </div>

            <div className="control_group w_50">
              <label>Price</label>
              <TextField
                variant="outlined"
                placeholder="Price"
                className="text_field"
                fullWidth
                hiddenLabel
                name="planAmount"
                id="planAmount"
                inputProps={{ maxLength: 80 }}
                value={formik.values.planAmount}
                onChange={(val) => {
                  if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.planAmount && formik.errors.planAmount
                }
                error={
                  formik.touched.planAmount && Boolean(formik.errors.planAmount)
                }
              />
            </div>

            <div className="control_group w_50">
              <label>Discount Price </label>
              <TextField
                variant="outlined"
                placeholder="Discount Price"
                className="text_field"
                fullWidth
                hiddenLabel
                name="discountAmount"
                id="discountAmount"
                inputProps={{ maxLength: 80 }}
                value={formik.values.discountAmount}
                onChange={(val) => {
                  if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.discountAmount && formik.errors.discountAmount
                }
                error={
                  formik.touched.discountAmount &&
                  Boolean(formik.errors.discountAmount)
                }
              />
            </div>

            <div className="control_group w_50">
              <label>Category</label>
              <Select
                fullWidth
                labelId="vehicleCategory"
                name="vehicleCategory"
                value={formik.values.vehicleCategory}
                displayEmpty
                onChange={(e) =>
                  formik.setFieldValue("vehicleCategory", e.target.value)
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.vehicleCategory &&
                  Boolean(formik.errors.vehicleCategory)
                }
              >
                <MenuItem disabled value="">
                  Select Category
                </MenuItem>
                {categories?.length
                  ? categories?.map((item) => (
                      <MenuItem key={item?._id} value={item?._id}>
                        {item?.name?.en || ""}
                      </MenuItem>
                    ))
                  : null}
              </Select>
              {formik.touched.vehicleCategory &&
                formik.errors.vehicleCategory && (
                  <FormHelperText error>
                    {formik.errors.vehicleCategory}
                  </FormHelperText>
                )}
            </div>

            <div className="control_group w_50">
              <label>Vehicle</label>
              <Select
                fullWidth
                labelId="vehicle"
                name="vehicle"
                multiple
                value={vehicleValue} // ← ALWAYS an array
                displayEmpty
                onChange={(e) => {
                  const raw = e.target.value;

                  const value = Array.isArray(raw)
                    ? raw
                    : typeof raw === "string"
                      ? raw.split(",")
                      : [];

                  formik.setFieldValue("vehicle", value);
                }}
                renderValue={(selected) => {
                  const sel = Array.isArray(selected) ? selected : [];
                  if (!sel.length) return "Select Vehicles";

                  return sel
                    .map((id) => vehicles.find((v) => v._id === id)?.title?.en)
                    .filter(Boolean)
                    .join(", ");
                }}
              >
                <MenuItem disabled value="">
                  Select Vehicles
                </MenuItem>

                {vehicles.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    <Checkbox checked={vehicleValue.includes(item._id)} />
                    {item.title?.en}
                  </MenuItem>
                ))}
              </Select>

              {formik.touched.vehicle && formik.errors.vehicle && (
                <FormHelperText error>{formik.errors.vehicle}</FormHelperText>
              )}
            </div>
            <div className="control_group w_50">
              <label>Mileage Limit</label>
              <TextField
                variant="outlined"
                placeholder="Mileage Limit"
                className="text_field"
                fullWidth
                hiddenLabel
                name="mileageLimit"
                id="mileageLimit"
                inputProps={{ maxLength: 80 }}
                value={formik.values.mileageLimit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.mileageLimit && formik.errors.mileageLimit
                }
                error={
                  formik.touched.mileageLimit &&
                  Boolean(formik.errors.mileageLimit)
                }
              />
            </div>

            <div className="control_group w_50">
              <label>Allowed Locations</label>
              <Select
                fullWidth
                labelId="allowedLocations"
                name="allowedLocations"
                value={formik.values.allowedLocations}
                displayEmpty
                onChange={(e) =>
                  formik.setFieldValue("allowedLocations", e.target.value)
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.allowedLocations &&
                  Boolean(formik.errors.allowedLocations)
                }
              >
                <MenuItem disabled value="">
                  Select Location
                </MenuItem>
                {cities?.length
                  ? cities?.map((item: any) => (
                      <MenuItem key={item?._id} value={item?._id}>
                        {item?.name?.en || ""}
                      </MenuItem>
                    ))
                  : null}
              </Select>
              {formik.touched.allowedLocations &&
                formik.errors.allowedLocations && (
                  <FormHelperText error>
                    {formik.errors.allowedLocations}
                  </FormHelperText>
                )}
            </div>

            <div className="control_group w_100">
              <label>Additional Benefits</label>

              {formik.values.additionalBenefits && id && (
                <EditText
                  content={formik.values.additionalBenefits}
                  setContent={(val) =>
                    formik.setFieldValue("additionalBenefits", val)
                  }
                />
              )}
              {!id && (
                <EditText
                  content={formik.values.additionalBenefits}
                  setContent={(val) =>
                    formik.setFieldValue("additionalBenefits", val)
                  }
                />
              )}

              {formik.touched.additionalBenefits &&
                formik.errors.additionalBenefits && (
                  <p className="err_msg">{formik.errors.additionalBenefits}</p>
                )}
            </div>
          </div>
          <div className="control_group"></div>
          <div className="form_btn end">
            <Button
              className="btn btn_secondary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddSubscription;
